// Drugs › Compare, the sheet: the Drug list's runs as rows, the targets they are modelled on as columns (the atlas's
// order), each cell the predicted occupancy at the run's scenario tinted on the occupancy scale, the primary target
// ringed. Hover reads the pair in full (the spread, Ki, the exposure); a click opens that drug on that target. A course
// without predictions shows the peak of its own occupancy, and a drug with no binding modelled says why.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { useAtlas } from '../app/store.ts';
import { IconButton } from '../ui/primitives.tsx';
import { RAMPS, sampleStops } from '../ui/scales.ts';
import { TARGET_LABEL } from './Traces.tsx';
import { ATLAS_TARGETS, INDEPENDENCE_POLICY, MEASURED, measuredFor, measuredText, occupancyText, rangeText, useCompare, type CompareCell, type CompareRow } from './compare.ts';

const cellColour = (v: number): string => {
  const [r, g, b] = sampleStops(RAMPS.occupancy, Math.min(1, Math.max(0, v)));
  return `rgb(${r}, ${g}, ${b})`;
};

const pct = (v: number) => `${Math.round(v * 100)} %`;
const spread = (c: CompareCell) => (c.low !== null && c.high !== null ? ` (${pct(c.low)}–${pct(c.high)})` : '');
const nm = (v: number) => (v >= 100 ? `${Math.round(v)} nM` : v >= 1 ? `${v.toFixed(1)} nM` : `${v.toFixed(2)} nM`);

function readoutText(row: CompareRow, cell: CompareCell | null, target: string | null): string {
  const dose = row.scenario ? ` · ${row.scenario}` : '';
  if (!cell) {
    if (row.note && (!target || target === row.primary)) return `${row.name}${dose} · ${row.note}`;
    return `${row.name}${dose} · ${target ? `${TARGET_LABEL(target)}: not modelled in this run` : row.note ?? ''}`;
  }
  const how = cell.from === 'course-peak' ? 'at the peak of its course' : row.exposureNm !== null ? `at ${nm(row.exposureNm)} free in the brain` : 'at the scenario’s exposure';
  const ki = cell.kiNm !== null ? ` · Ki ${nm(cell.kiNm)}` : '';
  const primary = cell.target === row.primary ? ' · primary target' : '';
  return `${row.name}${dose} · ${TARGET_LABEL(cell.target)} ${occupancyText(cell.central)} occupied${spread(cell)} ${how}${ki}${primary}`;
}

export function DrugCompare({ onClose }: { onClose: () => void }) {
  const table = useCompare((s) => s.table);
  const status = useCompare((s) => s.status);
  const why = useCompare((s) => s.why);
  const setChoice = useAtlas((s) => s.setChoice);
  const setTraceView = useAtlas((s) => s.setTraceView);
  const current = useAtlas((s) => s.choices.drug);
  const [hover, setHover] = useState<{ row: number; target: string | null } | null>(null);
  const sheet = useCallback((el: HTMLElement | null) => el?.focus(), []);
  // Esc closes the sheet before the app's shortcuts see the key.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);
  const pick = (row: CompareRow, target: string) => {
    setChoice('drug', row.value);
    setTraceView({ target });
    onClose();
  };
  const rows = table?.rows ?? [];
  const targets = table?.targets ?? [];
  const unnamed = useMemo(() => ATLAS_TARGETS.filter((t) => !targets.includes(t)), [targets]);
  const peaks = rows.filter((r) => r.cells.some((c) => c.from === 'course-peak'));
  const hovered = hover ? rows[hover.row] : null;
  const hoveredCell = hovered && hover?.target ? (hovered.cells.find((c) => c.target === hover.target) ?? null) : null;
  const hoveredMeasured = hovered && hover?.target ? measuredFor(hovered.value, hover.target) : null;
  const registered = [...new Set(MEASURED.flatMap((e) => e.studies.map((s) => s.registered.path)))];
  return (
    <div className="sheet-overlay" onClick={onClose} role="presentation">
      <section className="sheet drug-compare" role="dialog" aria-label="Compare drugs" aria-modal="true" tabIndex={-1} ref={sheet} data-label-exclude="" onClick={(e) => e.stopPropagation()}>
        <header className="sheet-head">
        <div>
          <h4>Where the drugs bind</h4>
          <p className="ba-type-caption muted">
            {table
              ? `${rows.length} drugs of the Drug list against the ${targets.length} targets they are modelled on · predicted occupancy at each run’s own scenario, equilibrium site binding O = C / (C + Ki) · ◇ measured human PET beside it where a study exists · hover a cell for the spread, click to open that drug on that target`
              : status === 'error'
                ? (why ?? 'The table didn’t load.')
                : 'Reading the runs’ headers…'}
          </p>
        </div>
        <IconButton icon={X} label="Close" onClick={onClose} />
      </header>
      {table ? (
        <div className="compare-body">
          <table className="compare-table" onMouseLeave={() => setHover(null)}>
            <thead>
              <tr>
                <th scope="col" className="compare-drug">
                  Drug
                </th>
                {targets.map((t) => (
                  <th key={t} scope="col" className="compare-target" title={t}>
                    {TARGET_LABEL(t)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={row.value} className={row.value === current ? 'current' : ''} data-drug={row.value.replace(/^pharma\//, '')}>
                  <th scope="row" className="compare-drug" onMouseEnter={() => setHover({ row: i, target: null })}>
                    <span className="compare-name">{row.name}</span>
                    {row.scenario ? <span className="compare-dose muted">{row.scenario}</span> : null}
                  </th>
                  {targets.map((t) => {
                    const cell = row.cells.find((c) => c.target === t) ?? null;
                    const primary = t === row.primary;
                    if (!cell)
                      return (
                        <td key={t} className={`compare-cell empty${primary ? ' primary' : ''}`} data-target={t} onMouseEnter={() => setHover({ row: i, target: t })}>
                          {primary ? <span className="compare-primary-mark" aria-label="primary target, no binding modelled">·</span> : null}
                        </td>
                      );
                    const pet = measuredFor(row.value, t);
                    const title = [readoutText(row, cell, t), ...(pet ? measuredText(pet) : [])].join('\n');
                    return (
                      <td key={t} className={`compare-cell${primary ? ' primary' : ''}${cell.from === 'course-peak' ? ' peak' : ''}${pet ? ' measured' : ''}`} data-target={t} data-occupancy={cell.central.toFixed(4)} data-measured={pet ? rangeText(pet.observed_range_percent) : undefined} onMouseEnter={() => setHover({ row: i, target: t })}>
                        <button type="button" className="compare-pick mono" style={{ background: cellColour(cell.central), color: cell.central > 0.55 ? '#06141a' : 'var(--text-1)' }} title={title} onClick={() => pick(row, t)}>
                          <span className="compare-predicted">
                            {occupancyText(cell.central)}
                            {cell.from === 'course-peak' ? <sup aria-hidden="true">*</sup> : null}
                          </span>
                          {pet ? (
                            <span className="compare-measured" aria-label={`measured by PET: ${rangeText(pet.observed_range_percent)} % in ${pet.studies.map((s) => s.id).join(', ')}`}>
                              {`◇ ${rangeText(pet.observed_range_percent)}`}
                            </span>
                          ) : null}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="compare-readout" aria-live="polite">
            <p>{hovered ? readoutText(hovered, hoveredCell, hover?.target ?? null) : 'Hover a cell for the drug, target, spread and Ki; the ring marks the primary target; ◇ is measured human PET, the study’s observed range.'}</p>
            {hoveredMeasured ? measuredText(hoveredMeasured).map((line) => <p key={line.slice(0, 40)} className="compare-measured-line">{line}</p>) : null}
          </div>
          <p className="ba-type-caption muted compare-foot">
            {`Occupancy is the header’s prediction at its scenario’s free brain concentration (central, with its low–high spread), from human exact-Ki profiles; it isn’t drawn here with any receptor density, and concentration alone does not establish efficacy.${peaks.length ? ` * ${peaks.map((r) => r.name).join(', ')}: the peak of the course’s own occupancy, no prediction in its header.` : ''}${unnamed.length ? ` None of these runs is modelled on the atlas’s other ${unnamed.length} PET targets (${unnamed.map(TARGET_LABEL).join(', ')}).` : ''} ◇ Measured human PET occupancy beside a prediction, the study’s observed range as the pharma lane’s pet_validation.json records it (${MEASURED.length} drug–target pairs, ${MEASURED.reduce((n, e) => n + e.studies.length, 0)} studies), with its dose, timing, tracer and region on hover; its independence rule: ${INDEPENDENCE_POLICY.replace(/\s+$/, '')} The registered comparisons: ${registered.join(', ')}.`}
          </p>
        </div>
      ) : null}
      </section>
    </div>
  );
}
